/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  DeleteAdminV1StoresStoreIdStockLocationsIdPathParameters,
  GetAdminV1OrgProductsProductIdStock200,
  GetAdminV1OrgProductsProductIdStockPathParameters,
  GetAdminV1StoresStoreIdInventory200,
  GetAdminV1StoresStoreIdInventoryLowStock200,
  GetAdminV1StoresStoreIdInventoryLowStockParams,
  GetAdminV1StoresStoreIdInventoryLowStockPathParameters,
  GetAdminV1StoresStoreIdInventoryMovements200,
  GetAdminV1StoresStoreIdInventoryMovementsParams,
  GetAdminV1StoresStoreIdInventoryMovementsPathParameters,
  GetAdminV1StoresStoreIdInventoryParams,
  GetAdminV1StoresStoreIdInventoryPathParameters,
  GetAdminV1StoresStoreIdInventoryVariantsVariantIdPathParameters,
  GetAdminV1StoresStoreIdStockLocations200,
  GetAdminV1StoresStoreIdStockLocationsPathParameters,
  InventoryItem,
  InventoryLevel,
  PatchAdminV1StoresStoreIdStockLocationsIdBody,
  PatchAdminV1StoresStoreIdStockLocationsIdPathParameters,
  PostAdminV1StoresStoreIdInventoryAdjustBody,
  PostAdminV1StoresStoreIdInventoryAdjustPathParameters,
  PostAdminV1StoresStoreIdStockLocationsBody,
  PostAdminV1StoresStoreIdStockLocationsPathParameters,
  PutAdminV1StoresStoreIdInventoryReorderPointBody,
  PutAdminV1StoresStoreIdInventoryReorderPointPathParameters,
  StockLocation
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Stock locations of the store, default first
 */
export const getAdminV1StoresStoreIdStockLocations = (
    { storeId }: GetAdminV1StoresStoreIdStockLocationsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdStockLocations200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdStockLocations200>(
      {url: `/admin/v1/stores/${storeId}/stock-locations`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Add a stock location
 */
export const postAdminV1StoresStoreIdStockLocations = (
    { storeId }: PostAdminV1StoresStoreIdStockLocationsPathParameters,
    postAdminV1StoresStoreIdStockLocationsBody: BodyType<PostAdminV1StoresStoreIdStockLocationsBody>,
 options?: SecondParameter<typeof customInstance<StockLocation>>,) => {
      return customInstance<StockLocation>(
      {url: `/admin/v1/stores/${storeId}/stock-locations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdStockLocationsBody
    },
      options);
    }
  /**
 * @summary Update a stock location or make it the default
 */
export const patchAdminV1StoresStoreIdStockLocationsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdStockLocationsIdPathParameters,
    patchAdminV1StoresStoreIdStockLocationsIdBody: BodyType<PatchAdminV1StoresStoreIdStockLocationsIdBody>,
 options?: SecondParameter<typeof customInstance<StockLocation>>,) => {
      return customInstance<StockLocation>(
      {url: `/admin/v1/stores/${storeId}/stock-locations/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdStockLocationsIdBody
    },
      options);
    }
  /**
 * @summary Delete an empty, non-default stock location
 */
export const deleteAdminV1StoresStoreIdStockLocationsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdStockLocationsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/stock-locations/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Defaults to the store's default location. With several `locationId`s a variant has one row per location, each with its own `locationId`; the top-level `locationId` is the first location listed. Variants that never had a movement show zeros. `lowStock=true` keeps only levels at or below their reorder point, `false` only those above it or without one
 * @summary Stock levels of tracked variants at one or more locations
 */
export const getAdminV1StoresStoreIdInventory = (
    { storeId }: GetAdminV1StoresStoreIdInventoryPathParameters,
    params?: GetAdminV1StoresStoreIdInventoryParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdInventory200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdInventory200>(
      {url: `/admin/v1/stores/${storeId}/inventory`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Levels at or below their reorder point
 */
export const getAdminV1StoresStoreIdInventoryLowStock = (
    { storeId }: GetAdminV1StoresStoreIdInventoryLowStockPathParameters,
    params?: GetAdminV1StoresStoreIdInventoryLowStockParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdInventoryLowStock200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdInventoryLowStock200>(
      {url: `/admin/v1/stores/${storeId}/inventory/low-stock`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Every location of the store, default first, with zeros where nothing moved yet, and the totals over them. Answers for a variant that does not track inventory too, so the admin can offer to turn tracking on
 * @summary One variant's stock at every location of the store
 */
export const getAdminV1StoresStoreIdInventoryVariantsVariantId = (
    { storeId, variantId }: GetAdminV1StoresStoreIdInventoryVariantsVariantIdPathParameters,
 options?: SecondParameter<typeof customInstance<InventoryItem>>,) => {
      return customInstance<InventoryItem>(
      {url: `/admin/v1/stores/${storeId}/inventory/variants/${variantId}`, method: 'GET'
    },
      options);
    }
  /**
 * Totals over each store's locations, for the stores the member can see. A store and variant without any stock row is absent: read it as zero
 * @summary Stock of a product's variants per store
 */
export const getAdminV1OrgProductsProductIdStock = (
    { productId }: GetAdminV1OrgProductsProductIdStockPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgProductsProductIdStock200>>,) => {
      return customInstance<GetAdminV1OrgProductsProductIdStock200>(
      {url: `/admin/v1/org/products/${productId}/stock`, method: 'GET'
    },
      options);
    }
  /**
 * adjust applies a signed delta, receive adds a supplier delivery, count sets the physical quantity. Stock never goes negative (409 INSUFFICIENT_STOCK). Every change is a ledger row and an audit entry
 * @summary Change on-hand stock with a reason
 */
export const postAdminV1StoresStoreIdInventoryAdjust = (
    { storeId }: PostAdminV1StoresStoreIdInventoryAdjustPathParameters,
    postAdminV1StoresStoreIdInventoryAdjustBody: BodyType<PostAdminV1StoresStoreIdInventoryAdjustBody>,
 options?: SecondParameter<typeof customInstance<InventoryLevel>>,) => {
      return customInstance<InventoryLevel>(
      {url: `/admin/v1/stores/${storeId}/inventory/adjust`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdInventoryAdjustBody
    },
      options);
    }
  /**
 * @summary Set or clear the low-stock threshold of a variant at a location
 */
export const putAdminV1StoresStoreIdInventoryReorderPoint = (
    { storeId }: PutAdminV1StoresStoreIdInventoryReorderPointPathParameters,
    putAdminV1StoresStoreIdInventoryReorderPointBody: BodyType<PutAdminV1StoresStoreIdInventoryReorderPointBody>,
 options?: SecondParameter<typeof customInstance<InventoryLevel>>,) => {
      return customInstance<InventoryLevel>(
      {url: `/admin/v1/stores/${storeId}/inventory/reorder-point`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdInventoryReorderPointBody
    },
      options);
    }
  /**
 * @summary Inventory ledger, newest first
 */
export const getAdminV1StoresStoreIdInventoryMovements = (
    { storeId }: GetAdminV1StoresStoreIdInventoryMovementsPathParameters,
    params?: GetAdminV1StoresStoreIdInventoryMovementsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdInventoryMovements200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdInventoryMovements200>(
      {url: `/admin/v1/stores/${storeId}/inventory/movements`, method: 'GET',
        params
    },
      options);
    }
  export type GetAdminV1StoresStoreIdStockLocationsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdStockLocations>>>
export type PostAdminV1StoresStoreIdStockLocationsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdStockLocations>>>
export type PatchAdminV1StoresStoreIdStockLocationsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdStockLocationsId>>>
export type DeleteAdminV1StoresStoreIdStockLocationsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdStockLocationsId>>>
export type GetAdminV1StoresStoreIdInventoryResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdInventory>>>
export type GetAdminV1StoresStoreIdInventoryLowStockResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdInventoryLowStock>>>
export type GetAdminV1StoresStoreIdInventoryVariantsVariantIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdInventoryVariantsVariantId>>>
export type GetAdminV1OrgProductsProductIdStockResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsProductIdStock>>>
export type PostAdminV1StoresStoreIdInventoryAdjustResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdInventoryAdjust>>>
export type PutAdminV1StoresStoreIdInventoryReorderPointResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdInventoryReorderPoint>>>
export type GetAdminV1StoresStoreIdInventoryMovementsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdInventoryMovements>>>
